import { PageActionsType, PageSettingsTabType } from '@wix/platform-editor-sdk';
import type { GetAppManifestFn } from '@wix/yoshi-flow-editor';
import { type EditorAppContext } from '@app/types/editor-app-context.type';
import {
  APP_ACTION_EVENTS,
  DELETE_BLOG_EVENT,
  EDITOR_ACTION_TYPE,
  MANAGE_POSTS_EVENT,
} from '../constants/events';
import { isModernPagesPanelEditor } from './environment';

/**
 * For local development of `communities-blog-pages-panel` package:
 * - Replace with https://localhost:37000
 */
const pagesPanelUrl = 'https://editor.wix.com/communities-blog-pages-panel';

type GetAppManifestParams = {
  sdkScriptSrc: string;
  context: EditorAppContext;
};

const getAppManifest = ({
  sdkScriptSrc,
  context,
}: GetAppManifestParams): ReturnType<GetAppManifestFn> => {
  const getPanelUrl = (slug: string) =>
    `${pagesPanelUrl}/${slug}?wix-editor-script=${sdkScriptSrc}&token=${context.appToken}&editor3=${context.isEditor3}`;

  const { t } = context.flowAPI.translations;
  const seoPageActions = isModernPagesPanelEditor()
    ? [
        {
          title: t('app-manifest.a11y'),
          type: PageSettingsTabType.A11Y,
        },
        {
          title: t('app-manifest.social-share'),
          type: PageSettingsTabType.SOCIAL_SHARE,
        },
      ]
    : [];

  return {
    pages: {
      pageActions: {
        default: [
          {
            type: PageActionsType.RENAME,
          },
          {
            title: t('app-manifest.delete-page'),
            event: DELETE_BLOG_EVENT,
            icon: 'deleteRadio',
          },
        ],
        postPage: [],
      },
      pageSettings: {
        default: [
          {
            title: t('app-manifest.page-info'),
            type: PageSettingsTabType.PAGE_INFO,
          },
          {
            title: t('app-manifest.layout'),
            type: PageSettingsTabType.LAYOUT,
          },
          {
            title: t('app-manifest.permissions'),
            type: PageSettingsTabType.PERMISSIONS,
          },
          {
            title: t('app-manifest.seo'),
            type: PageSettingsTabType.SEO,
          },
          ...seoPageActions,
        ],
        postPage: [
          {
            title: t('app-manifest.page-info'),
            url: getPanelUrl('page-info'),
            type: PageSettingsTabType.PAGE_INFO,
          },
          {
            title: t('app-manifest.layout'),
            type: PageSettingsTabType.LAYOUT,
          },
          {
            title: t('app-manifest.permissions'),
            type: PageSettingsTabType.PERMISSIONS,
          },
          {
            title: t('app-manifest.seo'),
            url: getPanelUrl('seo'),
            type: PageSettingsTabType.SEO,
          },
          ...seoPageActions,
        ],
      },
      applicationSettings: {
        default: {
          displayName: t('app-manifest.blog-pages'),
        },
      },
      applicationActions: {
        default: [
          {
            title: t('app-manifest.manage-posts'),
            event: MANAGE_POSTS_EVENT,
            icon: 'settingsAction',
          },
          {
            title: t('app-manifest.delete-blog'),
            event: DELETE_BLOG_EVENT,
            icon: 'deleteRadio',
          },
        ],
      },
      pageDescriptors: {
        default: {
          icon: 'blogPageType',
        },
      },
    },
    appDescriptor: {
      application: context.isEditor3
        ? {
            name: t('app-manifest.application.name'),
            pages: {
              images: [
                `${context.assetsUrl}/e3-my-business/feed-page.png`,
                `${context.assetsUrl}/e3-my-business/post-page.png`,
              ],
            },
            editorSetup: {
              title: t('app-manifest.editor-setup.title'),
              description: t('app-manifest.editor-setup.description'),
            },
          }
        : undefined,
      mainActions: context.isEditor3
        ? [
            {
              title: t('app-manifest.app-descriptor.create-post'),
              actionId: APP_ACTION_EVENTS.CREATE_POST,
              icon: 'appManager_addElementsAction',
            },
            {
              title: t('app-manifest.app-descriptor.manage-posts'),
              actionId: APP_ACTION_EVENTS.MANAGE_POSTS,
              icon: 'dashboard',
            },
            {
              title: t('app-manifest.app-descriptor.open-page-settings'),
              actionId: APP_ACTION_EVENTS.OPEN_POST_PAGE_SETTINGS,
              icon: 'edit',
            },
          ]
        : [
            {
              title: t('app-manifest.app-descriptor.create-post'),
              actionId: APP_ACTION_EVENTS.CREATE_POST,
              icon: 'appManager_addElementsAction',
            },
            {
              title: t('app-manifest.app-descriptor.manage-posts'),
              actionId: APP_ACTION_EVENTS.MANAGE_POSTS,
              icon: 'appManager_settingsAction',
            },
          ],
      customActions: context.isEditor3
        ? [
            {
              title: t('app-manifest.blog-settings'),
              actionId: APP_ACTION_EVENTS.OPEN_BLOG_SETTINGS_PANEL,
              icon: 'appManager_settingsAction',
              type: EDITOR_ACTION_TYPE,
            },
            {
              title: t('app-manifest.blog-help-center'),
              url: 'https://support.wix.com/en/article/wix-blog-about-the-wix-blog',
              icon: 'infoCircle',
              type: EDITOR_ACTION_TYPE,
            },
            {
              title: t('app-manifest.app-descriptor.read-blogging-guide'),
              icon: 'users',
              type: EDITOR_ACTION_TYPE,
              url: 'https://support.wix.com/en/article/wix-blog-about-the-wix-blog',
            },
          ]
        : [
            {
              title: t('app-manifest.app-descriptor.manage-blog-pages'),
              actionId: APP_ACTION_EVENTS.OPEN_BLOG_PAGES_PANEL,
              icon: 'appManager_pagesAction',
              type: EDITOR_ACTION_TYPE,
            },
            {
              title: t('app-manifest.app-descriptor.open-page-settings'),
              actionId: APP_ACTION_EVENTS.OPEN_POST_PAGE_SETTINGS,
              icon: 'customizeDesignButtonBrush',
              type: EDITOR_ACTION_TYPE,
            },
            {
              title: t('app-manifest.app-descriptor.add-blog-elements'),
              actionId: APP_ACTION_EVENTS.OPEN_BLOG_ADD_PANEL,
              icon: 'appManager_addElementsAction',
              type: EDITOR_ACTION_TYPE,
            },
            {
              title: t('app-manifest.app-descriptor.read-blogging-guide'),
              icon: 'sparkling-bulb-reg',
              type: EDITOR_ACTION_TYPE,
              url: 'https://www.wix-blog-community.com/guide',
            },
          ],
      defaultActions: {
        learnMoreKB: 'a7eeebda-8828-4a49-ba7e-d9b656caa197',
        upgrade: context.isEditor3
          ? {
              upgradeText: t('app-manifest.upgrade-text'),
              upgradeLinkText: t('app-manifest.upgrade-link-text'),
              upgradeType: 'SITE_UPGRADE',
            }
          : undefined,
      },
    },
  };
};

export default getAppManifest;
